import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BadgeCheck, Phone, FileText, Shield, Loader2, CheckCircle, XCircle, AlertCircle, Upload } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerificationPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Phone verification
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [demoOtp, setDemoOtp] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  
  // ID verification
  const [idType, setIdType] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [idDocument, setIdDocument] = useState(null);
  const [idLoading, setIdLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/verification', message: 'Please login to verify your account' } });
      return;
    }
    fetchStatus();
  }, [navigate]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestPhoneOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast({
        title: 'Error',
        description: 'Please enter a valid phone number',
        variant: 'destructive'
      });
      return;
    }

    setPhoneLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verification/phone/request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: phoneNumber })
      });

      const data = await response.json();

      if (response.ok) {
        setOtpSent(true);
        setDemoOtp(data.demo_otp || '');
        toast({
          title: 'OTP Sent',
          description: 'Please check your phone for the verification code'
        });
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to send OTP',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send OTP',
        variant: 'destructive'
      });
    } finally {
      setPhoneLoading(false);
    }
  };

  const verifyPhoneOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast({
        title: 'Error',
        description: 'Please enter a valid 6-digit OTP',
        variant: 'destructive'
      });
      return;
    }

    setPhoneLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verification/phone/verify`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: phoneNumber, otp })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Success!',
          description: 'Phone verified successfully'
        });
        fetchStatus();
        setOtpSent(false);
        setOtp('');
        setDemoOtp('');
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Invalid OTP',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to verify OTP',
        variant: 'destructive'
      });
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleIDUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Upload to server
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setIdDocument(data.url || data.file_url);
        toast({
          title: 'Uploaded',
          description: 'Document uploaded successfully'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to upload document',
        variant: 'destructive'
      });
    }
  };

  const submitIDVerification = async () => {
    if (!idType || !idNumber || !idDocument) {
      toast({
        title: 'Error',
        description: 'Please fill all fields and upload a document',
        variant: 'destructive'
      });
      return;
    }

    setIdLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verification/id/submit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id_type: idType,
          id_number: idNumber,
          id_document_url: idDocument
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Submitted!',
          description: 'Your ID is being reviewed'
        });
        fetchStatus();
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to submit',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit verification',
        variant: 'destructive'
      });
    } finally {
      setIdLoading(false);
    }
  };

  const getBadgeColor = (level) => {
    switch (level) {
      case 'fully_verified': return 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white';
      case 'id': return 'bg-green-500 text-white';
      case 'phone': return 'bg-blue-500 text-white';
      default: return 'bg-gray-200 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!status?.enabled) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Verification feature is currently not available. Please check back later.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3">
            <BadgeCheck className="h-10 w-10" />
            <div>
              <h1 className="text-4xl font-bold">Get Verified</h1>
              <p className="text-green-100">Earn trust badges to increase buyer confidence</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Status */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              Your Verification Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Badge className={`text-lg py-2 px-4 ${getBadgeColor(status?.verification_level)}`}>
                <BadgeCheck className="h-5 w-5 mr-2" />
                {status?.level_info?.label || 'Not Verified'}
              </Badge>
              <div className="flex gap-2">
                {status?.phone_verified && (
                  <Badge variant="outline" className="text-blue-600 border-blue-300">
                    <Phone className="h-3 w-3 mr-1" /> Phone ✓
                  </Badge>
                )}
                {status?.id_verified && (
                  <Badge variant="outline" className="text-green-600 border-green-300">
                    <FileText className="h-3 w-3 mr-1" /> ID ✓
                  </Badge>
                )}
              </div>
            </div>
            {status?.pending_verifications?.length > 0 && (
              <p className="text-sm text-yellow-600 mt-3 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                Pending: {status.pending_verifications.join(', ')} verification
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Phone Verification */}
          <Card className={status?.phone_verified ? 'border-green-200 bg-green-50' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className={`h-5 w-5 ${status?.phone_verified ? 'text-green-600' : 'text-blue-600'}`} />
                Phone Verification
              </CardTitle>
              <CardDescription>
                {status?.phone_verified 
                  ? 'Your phone number is verified'
                  : 'Verify your phone number with OTP'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {status?.phone_verified ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Verified</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="07XXXXXXXX"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      disabled={otpSent}
                    />
                  </div>
                  
                  {!otpSent ? (
                    <Button 
                      onClick={requestPhoneOTP} 
                      disabled={phoneLoading || !phoneNumber}
                      className="w-full"
                    >
                      {phoneLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                      ) : 'Send OTP'}
                    </Button>
                  ) : (
                    <>
                      <div>
                        <Label htmlFor="otp">Enter OTP</Label>
                        <Input
                          id="otp"
                          type="text"
                          placeholder="6-digit code"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                        />
                      </div>
                      
                      {demoOtp && (
                        <Alert className="bg-blue-50 border-blue-200">
                          <AlertDescription className="text-blue-700">
                            Demo OTP: <span className="font-mono font-bold">{demoOtp}</span>
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      <div className="flex gap-2">
                        <Button 
                          onClick={verifyPhoneOTP} 
                          disabled={phoneLoading || otp.length !== 6}
                          className="flex-1"
                        >
                          {phoneLoading ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</>
                          ) : 'Verify OTP'}
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setOtpSent(false);
                            setOtp('');
                            setDemoOtp('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ID Verification */}
          <Card className={status?.id_verified ? 'border-green-200 bg-green-50' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className={`h-5 w-5 ${status?.id_verified ? 'text-green-600' : 'text-purple-600'}`} />
                ID Verification
              </CardTitle>
              <CardDescription>
                {status?.id_verified 
                  ? 'Your ID is verified'
                  : status?.pending_verifications?.includes('id')
                    ? 'Your ID is being reviewed'
                    : 'Submit your ID document for verification'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {status?.id_verified ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Verified</span>
                </div>
              ) : status?.pending_verifications?.includes('id') ? (
                <div className="flex items-center gap-2 text-yellow-600">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="font-medium">Under Review</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>ID Type</Label>
                    <Select value={idType} onValueChange={setIdType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select ID type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nic">National ID Card (NIC)</SelectItem>
                        <SelectItem value="passport">Passport</SelectItem>
                        <SelectItem value="driving_license">Driving License</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="idNumber">ID Number</Label>
                    <Input
                      id="idNumber"
                      placeholder="Enter your ID number"
                      value={idNumber}
                      onChange={(e) => setIdNumber(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label>Upload Document</Label>
                    <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleIDUpload}
                        className="hidden"
                        id="id-upload"
                      />
                      <label htmlFor="id-upload" className="cursor-pointer">
                        {idDocument ? (
                          <div className="flex items-center justify-center gap-2 text-green-600">
                            <CheckCircle className="h-5 w-5" />
                            <span>Document uploaded</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-gray-500">
                            <Upload className="h-8 w-8" />
                            <span>Click to upload ID image</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={submitIDVerification} 
                    disabled={idLoading || !idType || !idNumber || !idDocument}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {idLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
                    ) : 'Submit for Review'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Benefits */}
        <Card className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle>Benefits of Verification</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <BadgeCheck className="h-6 w-6 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-medium">Trust Badge</h4>
                  <p className="text-sm text-gray-600">Display a verified badge on your listings</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Shield className="h-6 w-6 text-green-600 mt-1" />
                <div>
                  <h4 className="font-medium">Higher Visibility</h4>
                  <p className="text-sm text-gray-600">Verified sellers rank higher in search</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-purple-600 mt-1" />
                <div>
                  <h4 className="font-medium">Buyer Confidence</h4>
                  <p className="text-sm text-gray-600">Buyers trust verified sellers more</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerificationPage;
